<template>
  <div class="case-management">
    <!-- 列表模式 -->
    <template v-if="mode === 'list'">
      <div class="page-header d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-1">案例题管理</h4>
          <p class="text-muted mb-0">{{ bankName || `题库 #${bankId}` }} 下的案例（材料 + 子题）</p>
        </div>
        <div>
          <router-link to="/admin/question-banks/import/case-study" class="btn btn-outline-info me-2">
            导入案例题
          </router-link>
          <button class="btn btn-outline-secondary me-2" @click="goBackToBank">返回题库</button>
          <button class="btn btn-primary" @click="startCreate">新增案例题</button>
        </div>
      </div>

      <div v-if="loading" class="text-center py-4">加载中…</div>
      <div v-else-if="cases.length === 0" class="empty-tip text-center text-muted py-4">
        暂无案例题。可点击"新增案例题"逐条创建，或用
        <router-link to="/admin/question-banks/import/case-study">案例题导入</router-link>
        批量入库（大模型解析 JSON + 图片目录）。
      </div>
      <div v-else class="case-list">
        <div v-for="item in cases" :key="item.id" class="case-list-item card mb-2">
          <div class="card-body d-flex justify-content-between align-items-center">
            <div class="min-w-0">
              <span :class="['badge', item.isActive ? 'bg-success' : 'bg-secondary', 'me-2']">
                {{ item.isActive ? '启用' : '停用' }}
              </span>
              <strong class="me-2">{{ item.title }}</strong>
              <span class="text-muted">子题 {{ item.questions.length }} 道</span>
            </div>
            <div class="flex-shrink-0">
              <button class="btn btn-sm btn-outline-primary me-2" @click="startEdit(item.id)">编辑</button>
              <button class="btn btn-sm btn-outline-danger" @click="removeCase(item)">删除</button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 编辑模式 -->
    <template v-else>
      <div class="page-header d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-1">{{ isEditRoute ? '编辑案例题' : '新增案例题' }}</h4>
          <p class="text-muted mb-0">案例材料支持文本段与图片；子题支持单选/多选/判断/填空/简答</p>
        </div>
        <button class="btn btn-outline-secondary" @click="backToList">返回列表</button>
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <div class="mb-3">
            <label class="form-label">案例标题 <span class="text-danger">*</span></label>
            <input v-model="form.title" type="text" class="form-control" maxlength="200" placeholder="如：案例1" />
          </div>

          <!-- 材料块编辑 -->
          <label class="form-label">案例材料（题干）</label>
          <div v-for="(block, index) in form.blocks" :key="index" class="block-editor mb-2">
            <div class="d-flex align-items-start gap-2">
              <div class="flex-grow-1">
                <textarea
                  v-if="block.type === 'text'"
                  v-model="block.content"
                  class="form-control"
                  rows="3"
                  placeholder="案例文本段落"
                ></textarea>
                <div v-else class="image-block">
                  <img v-if="block.url" :src="block.url" class="img-thumbnail mb-1" style="max-height: 160px" />
                  <div class="d-flex gap-2 align-items-center">
                    <label class="btn btn-sm btn-outline-primary mb-0">
                      {{ block.url ? '更换图片' : '选择图片' }}
                      <input type="file" accept="image/*" hidden @change="onBlockImageChange($event, block)" />
                    </label>
                    <input v-model="block.alt" type="text" class="form-control form-control-sm" placeholder="图片说明（可选）" />
                  </div>
                  <div v-if="block.uploading" class="text-muted small mt-1">上传中…</div>
                  <div v-if="block.error" class="text-danger small mt-1">{{ block.error }}</div>
                </div>
              </div>
              <div class="btn-group-vertical">
                <button class="btn btn-sm btn-outline-secondary" :disabled="index === 0" @click="moveBlock(index, -1)">↑</button>
                <button class="btn btn-sm btn-outline-secondary" :disabled="index === form.blocks.length - 1" @click="moveBlock(index, 1)">↓</button>
                <button class="btn btn-sm btn-outline-danger" @click="form.blocks.splice(index, 1)">删</button>
              </div>
            </div>
          </div>
          <div class="mb-3">
            <button class="btn btn-sm btn-outline-primary me-2" @click="addTextBlock">+ 文本段</button>
            <button class="btn btn-sm btn-outline-primary" @click="addImageBlock">+ 图片</button>
          </div>
        </div>
      </div>

      <!-- 子题编辑 -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="form-label mb-0">子题（按顺序作答与判分）</label>
            <button class="btn btn-sm btn-primary" @click="addSubQuestion">+ 添加子题</button>
          </div>

          <div v-for="(sub, index) in form.subQuestions" :key="index" class="sub-question card mb-3">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="badge bg-info">子题 {{ index + 1 }}</span>
                <div>
                  <button class="btn btn-sm btn-outline-secondary me-1" :disabled="index === 0" @click="moveSubQuestion(index, -1)">↑</button>
                  <button class="btn btn-sm btn-outline-secondary me-1" :disabled="index === form.subQuestions.length - 1" @click="moveSubQuestion(index, 1)">↓</button>
                  <button class="btn btn-sm btn-outline-danger" @click="form.subQuestions.splice(index, 1)">删除</button>
                </div>
              </div>

              <div class="row g-2 mb-2">
                <div class="col-md-3">
                  <label class="form-label">题型</label>
                  <select v-model="sub.type" class="form-select" @change="onSubTypeChange(sub)">
                    <option v-for="option in SUB_TYPE_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">难度</label>
                  <select v-model.number="sub.difficulty" class="form-select">
                    <option :value="1">简单</option>
                    <option :value="2">中等</option>
                    <option :value="3">困难</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">章节（可选）</label>
                  <input v-model="sub.chapter" type="text" class="form-control" maxlength="200" />
                </div>
              </div>

              <div class="mb-2">
                <label class="form-label">题干 <span class="text-danger">*</span></label>
                <textarea v-model="sub.question" class="form-control" rows="2" placeholder="子题题干"></textarea>
              </div>

              <!-- 选择题选项 -->
              <div v-if="sub.type === 'singleChoice' || sub.type === 'multipleChoice'" class="mb-2">
                <label class="form-label">
                  选项与正确答案
                  <span class="text-muted">（{{ sub.type === 'singleChoice' ? '单选：点选正确项' : '多选：勾选全部正确项' }}）</span>
                </label>
                <div v-for="(option, optionIndex) in sub.optionList" :key="optionIndex" class="d-flex align-items-center gap-2 mb-1">
                  <input
                    v-if="sub.type === 'singleChoice'"
                    v-model="sub.answer"
                    type="radio"
                    class="form-check-input"
                    :name="`correct-${index}`"
                    :value="option.key"
                  />
                  <input
                    v-else
                    v-model="sub.answerArray"
                    type="checkbox"
                    class="form-check-input"
                    :value="option.key"
                  />
                  <strong class="option-key">{{ option.key }}</strong>
                  <input v-model="option.value" type="text" class="form-control form-control-sm" placeholder="选项内容" />
                  <button class="btn btn-sm btn-outline-danger" :disabled="sub.optionList.length <= 2" @click="removeOption(sub, optionIndex)">×</button>
                </div>
                <button class="btn btn-sm btn-outline-secondary" :disabled="sub.optionList.length >= 8" @click="addOption(sub)">+ 选项</button>
              </div>

              <!-- 判断题 -->
              <div v-else-if="sub.type === 'trueFalse'" class="mb-2">
                <label class="form-label">正确答案</label>
                <select v-model="sub.answer" class="form-select">
                  <option value="true">正确</option>
                  <option value="false">错误</option>
                </select>
              </div>

              <!-- 填空/简答参考答案 -->
              <div v-else class="mb-2">
                <label class="form-label">{{ sub.type === 'fillBlank' ? '参考答案' : '参考答案（考生自评依据）' }}</label>
                <textarea v-model="sub.answer" class="form-control" rows="2"></textarea>
              </div>

              <div>
                <label class="form-label">解析（可选）</label>
                <textarea v-model="sub.explanation" class="form-control" rows="2" placeholder="试题解析"></textarea>
              </div>
            </div>
          </div>
          <div v-if="form.subQuestions.length === 0" class="text-muted text-center py-3">
            尚无子题，点击"添加子题"开始
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end gap-2 pb-4">
        <button class="btn btn-outline-secondary" @click="backToList">取消</button>
        <button class="btn btn-primary" :disabled="saving" @click="saveCase">
          {{ saving ? '保存中…' : '保存案例题' }}
        </button>
      </div>
      <div v-if="formError" class="alert alert-danger">{{ formError }}</div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { questionBanksApi } from '@/api/questionBanks'
import type { CaseMaterialDetail, CaseSubQuestionRequest } from '@/api/questionBanks'
import { uploadApi } from '@/api/upload'
import type { CaseMaterialBlock } from '@/types'

const route = useRoute()
const router = useRouter()

const SUB_TYPE_OPTIONS = [
  { value: 'singleChoice', label: '单选题' },
  { value: 'multipleChoice', label: '多选题' },
  { value: 'trueFalse', label: '判断题' },
  { value: 'fillBlank', label: '填空题' },
  { value: 'essay', label: '简答题' }
] as const

interface SubQuestionForm extends CaseSubQuestionRequest {
  optionList: Array<{ key: string; value: string }>
  answerArray: string[]
}

/** 编辑器内部材料块：图片块附带上传状态 */
type EditorBlock = CaseMaterialBlock & { uploading?: boolean; error?: string }

const bankId = computed(() => Number(route.params.bankId))
// 用路由名区分三种模式：列表 / 新建 / 编辑。
// 不能靠 params.caseId === 'new' 判断——新建路由没有 caseId 参数，该条件恒为假。
const isListRoute = computed(() => route.name === 'CaseQuestionList')
const isEditRoute = computed(() => route.name === 'CaseQuestionEdit')
const mode = computed(() => (isListRoute.value ? 'list' : 'edit'))
const caseId = computed(() => (isEditRoute.value && route.params.caseId ? Number(route.params.caseId) : null))

const bankName = ref('')
const loading = ref(false)
const saving = ref(false)
const formError = ref('')
const cases = ref<CaseMaterialDetail[]>([])

interface EditableImageBlock extends CaseMaterialBlock {
  type: 'image'
  url: string
  uploading?: boolean
  error?: string
}

const form = reactive({
  title: '',
  blocks: [] as EditorBlock[],
  subQuestions: [] as SubQuestionForm[]
})

onMounted(() => {
  void initPage()
})

// 列表/新建/编辑三条路由共用同一组件，Vue Router 会复用组件实例（onMounted 不再触发），
// 因此路由变化时必须重新按目标模式加载数据，否则会出现"编辑页显示空表单"。
watch(
  () => [route.name, route.params.caseId, route.params.bankId],
  () => {
    void initPage()
  },
)

const initPage = async () => {
  formError.value = ''
  if (bankId.value) {
    await loadBankName()
  }
  if (isListRoute.value) {
    await loadCases()
    return
  }
  if (isEditRoute.value && caseId.value != null) {
    await loadCase(caseId.value)
    return
  }
  resetCreateForm()
}

const loadBankName = async () => {
  try {
    const bank = await questionBanksApi.getQuestionBank(bankId.value)
    bankName.value = bank.description || bank.subdomain || `题库 #${bankId.value}`
  } catch {
    bankName.value = ''
  }
}

const loadCases = async () => {
  loading.value = true
  try {
    cases.value = await questionBanksApi.getCaseMaterials(bankId.value)
  } catch (error: any) {
    ElMessage.error(error.response?.data?.message || '加载案例列表失败')
  } finally {
    loading.value = false
  }
}

const loadCase = async (id: number) => {
  loading.value = true
  try {
    const detail = await questionBanksApi.getCaseMaterial(id)
    form.title = detail.title
    form.blocks = JSON.parse(JSON.stringify(detail.blocks))
    form.subQuestions = detail.questions.map((question) => {
      const sub = normalizeSubForm({
        type: question.type as CaseSubQuestionRequest['type'],
        chapter: question.chapter,
        question: question.question,
        options: question.options,
        answer: question.answer,
        explanation: question.explanation,
        difficulty: question.difficulty
      })
      return sub
    })
  } catch (error: any) {
    ElMessage.error(error.response?.data?.message || '加载案例失败')
    backToList()
  } finally {
    loading.value = false
  }
}

const normalizeSubForm = (input: Partial<CaseSubQuestionRequest>): SubQuestionForm => {
  const sub: SubQuestionForm = {
    type: input.type || 'singleChoice',
    chapter: input.chapter || '',
    question: input.question || '',
    answer: input.answer || '',
    explanation: input.explanation || '',
    difficulty: input.difficulty || 1,
    optionList: [],
    answerArray: []
  }
  if (sub.type === 'singleChoice' || sub.type === 'multipleChoice') {
    const options = input.options && Object.keys(input.options).length > 0
      ? input.options
      : { A: '', B: '', C: '', D: '' }
    sub.optionList = Object.entries(options).map(([key, value]) => ({ key, value: String(value) }))
    sub.answerArray = sub.type === 'multipleChoice'
      ? String(input.answer || '').split(',').map(item => item.trim()).filter(Boolean)
      : []
  }
  return sub
}

const startCreate = () => {
  // 走新建路由，由 initPage 统一初始化表单
  router.push(`/admin/question-banks/${bankId.value}/cases/new`)
}

const startEdit = (id: number) => {
  router.push(`/admin/question-banks/${bankId.value}/cases/${id}/edit`)
}

const goBackToBank = () => {
  router.push(`/admin/question-banks/${bankId.value}`)
}

const backToList = () => {
  router.push(`/admin/question-banks/${bankId.value}/cases`)
}

/** 清空表单并进入新建模式（initPage 也会在进入新建路由时做同样的初始化） */
const resetCreateForm = () => {
  form.title = ''
  form.blocks = [{ type: 'text', content: '' }]
  form.subQuestions = [normalizeSubForm({ type: 'singleChoice' })]
}

// ---- 材料块 ----
const addTextBlock = () => {
  form.blocks.push({ type: 'text', content: '' })
}

const addImageBlock = () => {
  form.blocks.push({ type: 'image', url: '' } as EditableImageBlock)
}

const moveBlock = (index: number, direction: -1 | 1) => {
  const target = index + direction
  if (target < 0 || target >= form.blocks.length) return
  const [block] = form.blocks.splice(index, 1)
  form.blocks.splice(target, 0, block)
}

const onBlockImageChange = async (event: Event, block: EditorBlock) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  block.uploading = true
  block.error = ''
  try {
    const [uploaded] = await uploadApi.uploadQuestionImages([file])
    if (!uploaded) throw new Error('上传失败')
    block.url = uploaded.url
  } catch (error: any) {
    block.error = error.response?.data?.message || error.message || '上传失败'
  } finally {
    block.uploading = false
  }
}

// ---- 子题 ----
const addSubQuestion = () => {
  form.subQuestions.push(normalizeSubForm({ type: 'singleChoice' }))
}

const onSubTypeChange = (sub: SubQuestionForm) => {
  if (sub.type === 'singleChoice' || sub.type === 'multipleChoice') {
    if (sub.optionList.length === 0) {
      sub.optionList = Object.entries({ A: '', B: '', C: '', D: '' }).map(([key, value]) => ({ key, value }))
    }
    if (sub.type === 'multipleChoice') {
      sub.answerArray = []
    } else {
      sub.answer = ''
    }
  }
}

const addOption = (sub: SubQuestionForm) => {
  const nextKey = String.fromCharCode(65 + sub.optionList.length)
  sub.optionList.push({ key: nextKey, value: '' })
}

const removeOption = (sub: SubQuestionForm, index: number) => {
  const [removed] = sub.optionList.splice(index, 1)
  sub.optionList.forEach((option, optionIndex) => {
    option.key = String.fromCharCode(65 + optionIndex)
  })
  if (sub.type === 'singleChoice' && sub.answer === removed?.key) {
    sub.answer = ''
  }
  if (sub.type === 'multipleChoice') {
    sub.answerArray = sub.answerArray.filter(key => key !== removed?.key)
  }
}

const moveSubQuestion = (index: number, direction: -1 | 1) => {
  const target = index + direction
  if (target < 0 || target >= form.subQuestions.length) return
  const [sub] = form.subQuestions.splice(index, 1)
  form.subQuestions.splice(target, 0, sub)
}

// ---- 校验与保存 ----
const validateForm = (): string => {
  if (!form.title.trim()) return '请填写案例标题'
  if (form.blocks.length === 0) return '请至少添加一个材料块'
  for (const [index, block] of form.blocks.entries()) {
    if (block.type === 'text' && !block.content?.trim()) return `材料块 ${index + 1}：文本段不能为空`
    if (block.type === 'image' && !block.url) return `材料块 ${index + 1}：请先上传图片`
  }
  if (form.subQuestions.length === 0) return '案例题至少需要一道子题'
  for (const [index, sub] of form.subQuestions.entries()) {
    const label = `子题 ${index + 1}`
    if (!sub.question.trim()) return `${label}：题干不能为空`
    if (sub.type === 'singleChoice' || sub.type === 'multipleChoice') {
      if (sub.optionList.some(option => !option.value.trim())) return `${label}：选项内容不能为空`
      if (sub.type === 'singleChoice' && !sub.answer) return `${label}：请选择正确答案`
      if (sub.type === 'multipleChoice' && sub.answerArray.length < 2) return `${label}：多选题至少勾选两个正确项`
    } else if (sub.type !== 'essay' && !String(sub.answer).trim()) {
      return `${label}：请填写参考答案`
    }
  }
  return ''
}

const buildPayload = () => ({
  title: form.title.trim(),
  blocks: form.blocks.map((block): CaseMaterialBlock => block.type === 'text'
    ? { type: 'text', content: block.content || '' }
    : { type: 'image', url: block.url || '', ...(block.alt ? { alt: block.alt } : {}) }),
  subQuestions: form.subQuestions.map(sub => {
    const options = sub.type === 'singleChoice' || sub.type === 'multipleChoice'
      ? Object.fromEntries(sub.optionList.map(option => [option.key, option.value]))
      : undefined
    const answer = sub.type === 'multipleChoice' ? [...sub.answerArray].sort().join(',') : sub.answer
    return {
      type: sub.type,
      chapter: sub.chapter?.trim() || undefined,
      question: sub.question.trim(),
      options,
      answer,
      explanation: sub.explanation?.trim() || undefined,
      difficulty: sub.difficulty
    }
  })
})

const saveCase = async () => {
  formError.value = validateForm()
  if (formError.value) return
  saving.value = true
  try {
    if (isEditRoute.value && caseId.value != null) {
      await questionBanksApi.updateCaseMaterial(caseId.value, buildPayload())
      ElMessage.success('案例题已更新')
    } else {
      await questionBanksApi.createCaseMaterial(bankId.value, buildPayload())
      ElMessage.success('案例题已创建')
    }
    backToList()
  } catch (error: any) {
    formError.value = error.response?.data?.message || '保存失败，请稍后重试'
  } finally {
    saving.value = false
  }
}

const removeCase = async (item: CaseMaterialDetail) => {
  if (!window.confirm(`确认删除案例"${item.title}"？其 ${item.questions.length} 道子题将一并删除。`)) {
    return
  }
  try {
    await questionBanksApi.deleteCaseMaterial(item.id)
    ElMessage.success('案例题已删除')
    await loadCases()
  } catch (error: any) {
    ElMessage.error(error.response?.data?.message || '删除失败')
  }
}
</script>

<style scoped>
.case-management {
  padding: 20px;
}

.page-header h4 {
  margin: 0;
}

.min-w-0 {
  min-width: 0;
}

.block-editor textarea {
  background: #fff;
}

.image-block img {
  display: block;
}

.option-key {
  width: 20px;
  text-align: center;
}

.sub-question .card-body {
  background: #fafbfc;
}
</style>
